import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { EpcotConfig, QuickBetConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CurrencyPipe } from '@frontend/sports/common/core/feature/money';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { DsButton } from '@frontend/ui/button';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { EpcotNotificationComponent } from 'packages/sports/web/app/src/common/epcotNotifications/notification.component';
import { ItemComponent } from 'packages/sports/web/app/src/common/item/item.component';
import { AlertButtonComponent } from 'packages/sports/web/app/src/native-alerts/alert-button/alert-button.component';
import { AlertConfigService } from 'packages/sports/web/app/src/native-alerts/alert-config.service';
import { NativeAlertsModule } from 'packages/sports/web/app/src/native-alerts/native-alerts.module';
import { Observable, map } from 'rxjs';

import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { ISucceededSlipResult } from '../../../betplacement/models';
import BetslipDigitalResultModule from '../../../result/betslip-digital-result.module';
import { IBetslipResultSummaryViewModel, TokenPayoutSummary } from '../../../result/models/betslip-result-summary.model';
import { betslipResultSummaryStateSelector } from '../../../result/selectors';
import { BetslipResultConfigurationBase } from '../../../result/services/result-configuration.service';
import { provideSummaryMessages } from '../../../result/utils/result-summary.utils';
import BetslipShareMyBetModule from '../../../share-my-bet/betslip-share-my-bet.module';
import WalletTypeContainerModule from '../../../wallet-type/wallet-type-container.module';
import { QuickBetActions } from '../../quick-bet-actions';
import { selectIsQuickBetInOverlay } from '../../quick-bet.selectors';
import { QuickBetUtils } from '../../services/quick-bet-utils.service';

@Component({
    selector: 'bs-quick-bet-confirmation-detailed',
    templateUrl: './quick-bet-confirmation-detailed.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-confirmation-detailed.component.scss'],
    imports: [
        DsButtonIcon,
        TrustPipe,
        IconCustomComponent,
        CommonModule,
        DsButton,
        BetslipShareMyBetModule,
        EpcotNotificationComponent,
        BetslipDigitalResultModule,
        WalletTypeContainerModule,
        NativeAlertsModule,
        ItemComponent,
        CurrencyPipe,
    ],
})
export class QuickBetConfirmationDetailedComponent implements OnInit, OnDestroy {
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    resultState = input.required<ISucceededSlipResult>();
    userPoints = input<number | undefined>();
    @Output() closeConfirmation = new EventEmitter<boolean>();

    @ViewChild(AlertButtonComponent) nativeAlertButton?: AlertButtonComponent;

    isUserBalanceEnabled: boolean;
    messages: IBetslipMessages;
    liveAlert = false;

    summary$: Observable<Nullable<IBetslipResultSummaryViewModel>>;
    inOverlay = toSignal(this.store.select(selectIsQuickBetInOverlay));

    constructor(
        public sitecore: Sitecore,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messagesProvider: IBetslipMessagesProviderService,
        private store: Store,
        private quickBetConfig: QuickBetConfig,
        private accountBalanceService: AccountBalanceService,
        private alertConfigService: AlertConfigService,
        public resultConfiguration: BetslipResultConfigurationBase,
        private epcotConfig: EpcotConfig,
        private quickBetUtils: QuickBetUtils,
    ) {
        this.isUserBalanceEnabled = this.quickBetConfig.isUserBalanceEnabled;
        this.messages = messagesProvider.getMessages();

        this.summary$ = this.store.select(betslipResultSummaryStateSelector).pipe(
            map((summary) =>
                provideSummaryMessages(summary, false, {
                    BoostedWinnings: this.messages.common.BoostedWinnings,
                    numberOfBets: this.messages.result.numberOfBets,
                    possibleWinnings: this.messages.result.possibleWinnings,
                    possibleWinningsGross: this.messages.result.possibleWinningsGross,
                    possibleWinningsNet: this.messages.result.possibleWinningsNet,
                    stake: this.messages.result.stake,
                    totalStake: this.messages.result.totalStake,
                    totalOdds: this.messages.result.totalOdds,
                    freebetText: this.messages.crmTokens.oddsBoost.betslipSummary.PayoutTypeFreebet,
                }),
            ),
        );
    }

    ngOnInit(): void {
        this.liveAlert =
            this.resultState().picks.filter(
                (res: any) => res.clientPick.liveAlert === true && this.alertConfigService.isSportEnabled(res.clientPick.sportId),
            ).length > 0;

        this.quickBetUtils.freezeScroll();
    }

    closeResultScreen(): void {
        this.closeConfirmation.emit(true);
    }

    navigateToMyBets(): void {
        this.store.dispatch(QuickBetActions.goToMyBets({ inPopup: this.epcotConfig.isEnabled }));
    }

    isNativeAlertEnabled(): boolean {
        return this.alertConfigService.isEnabled();
    }

    nativeAlertClicked(evt: Event): void {
        if (this.nativeAlertButton) {
            this.nativeAlertButton.clicked(evt);
        }
    }

    getSlipTokenPayoutSummary(slip: ISucceededSlipResult, tokenPayoutSummaries?: TokenPayoutSummary[]): TokenPayoutSummary | undefined {
        return tokenPayoutSummaries?.find((summary) => summary.betNumber === slip.betNumber);
    }

    get accountBalance(): number | undefined {
        return this.accountBalanceService.selectedWallet?.amount;
    }

    ngOnDestroy(): void {
        this.quickBetUtils.tryReleaseScroll();
    }
}
